import React from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  styled,
  useTheme,
  Card,
  CardContent,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { GET_COMPANY_INVITES } from 'src/utils/gql/queries/companyInvite';
import { DELETE_INVITE } from 'src/utils/gql/mutations/companyInvite';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { CompanyInvite } from 'src/utils/gql/types';
import { useState } from 'react';

// Styled components per il design responsive
const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',

  [theme.breakpoints.down('sm')]: {
    gap: '12px',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 600,
  marginBottom: '16px',

  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
    marginBottom: '12px',
  },
}));

const CustomTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    padding: '16px 12px',
    textAlign: 'center', // Centra tutto il contenuto delle celle
    verticalAlign: 'middle', // Centra verticalmente
  },

  [theme.breakpoints.down('md')]: {
    '& .MuiTableCell-root': {
      padding: '12px 8px',
      fontSize: '14px',
    },
  },

  [theme.breakpoints.down('sm')]: {
    display: 'none', // Nascondi tabella su mobile
  },
}));

const CustomTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-root': {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '14px',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    textAlign: 'center', // Centra anche gli header
    verticalAlign: 'middle',
  },

  [theme.breakpoints.down('md')]: {
    '& .MuiTableCell-root': {
      fontSize: '13px',
    },
  },
}));

// Componente Card per mobile
const MobileCard = styled(Card)(({ theme }) => ({
  marginBottom: '16px',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease',

  '&:hover': {
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
  },
}));

const MobileCardContent = styled(CardContent)(({ theme }) => ({
  padding: '16px',

  '&:last-child': {
    paddingBottom: '16px',
  },
}));

const MobileInfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',

  '&:last-child': {
    marginBottom: 0,
  },
}));

const MobileLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '14px',
  fontWeight: 500,
}));

const MobileValue = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  textAlign: 'right',
  maxWidth: '70%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const MobileActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: `1px solid ${theme.palette.grey[100]}`,
}));

export const InviteList: React.FC = () => {
  const snackbar = useSnackbar();
  const theme = useTheme();
  const [deletingInviteId, setDeletingInviteId] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(GET_COMPANY_INVITES, {
    onError: (error) => {
      snackbar(error.message);
    },
  });

  const [deleteInvite] = useMutation(DELETE_INVITE, {
    onCompleted: () => {
      snackbar('Invite deleted successfully', 'success');
      setDeletingInviteId(null);
      refetch();
    },
    onError: (error) => {
      snackbar(error.message);
      setDeletingInviteId(null);
    },
  });

  const handleDeleteInvite = (inviteId: string) => {
    if (
      window.confirm(
        'Are you sure you want to delete this invite? This action cannot be undone.',
      )
    ) {
      setDeletingInviteId(inviteId);
      deleteInvite({
        variables: { inviteId },
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const invites = (data?.getCompanyInvites || []).filter(
    (invite: CompanyInvite) =>
      invite.status !== 'cancelled' && invite.status !== 'accepted',
  );

  return (
    <Container>
      <Title variant="h6">Company Invites ({invites.length})</Title>

      {invites.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No invites found
        </Typography>
      ) : (
        <>
          {/* Tabella per desktop e tablet */}
          <TableContainer
            component={Paper}
            sx={{ boxShadow: 'none', display: { xs: 'none', sm: 'block' } }}
          >
            <CustomTable>
              <CustomTableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </CustomTableHead>
              <TableBody>
                {invites.map((invite: CompanyInvite) => (
                  <TableRow key={invite.id}>
                    <TableCell>
                      {invite.firstName} {invite.lastName}
                    </TableCell>
                    <TableCell>{invite.email}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          justifyContent: 'center',
                        }}
                      >
                        <Tooltip title="Delete invite">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteInvite(invite.id)}
                            disabled={deletingInviteId === invite.id}
                            color="error"
                            sx={{
                              border: '2px solid #d32f2f',
                              backgroundColor: '#ffebee',
                              minWidth: '32px',
                              minHeight: '32px',
                              '&:hover': {
                                backgroundColor: '#ffcdd2',
                              },
                            }}
                          >
                            {deletingInviteId === invite.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </CustomTable>
          </TableContainer>

          {/* Cards per mobile */}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            {invites.map((invite: CompanyInvite) => (
              <MobileCard key={invite.id}>
                <MobileCardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontSize: '16px', fontWeight: 600 }}
                    >
                      {invite.firstName} {invite.lastName}
                    </Typography>
                  </Box>

                  <MobileInfoRow>
                    <MobileLabel>Email:</MobileLabel>
                    <MobileValue>{invite.email}</MobileValue>
                  </MobileInfoRow>

                  <MobileActions>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Delete invite">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteInvite(invite.id)}
                          color="error"
                          disabled={deletingInviteId === invite.id}
                          sx={{
                            border: '1px solid #d32f2f',
                            backgroundColor: '#ffebee',
                            '&:hover': {
                              backgroundColor: '#ffcdd2',
                            },
                          }}
                        >
                          {deletingInviteId === invite.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </MobileActions>
                </MobileCardContent>
              </MobileCard>
            ))}
          </Box>
        </>
      )}
    </Container>
  );
};
